import { z } from 'zod';

/**
 * Communities (PRD 4.3). Private test communities, one per project at `/test/<address>`;
 * review communities, one per version in review, on the admin site (M22); private team
 * communities staff make in the admin site, at `/team/<address>` (M28). Game, genre and
 * topic communities reuse the same tables later.
 */
export const COMMUNITY_KINDS = ['test', 'game', 'genre', 'topic', 'review', 'team'] as const;
export type CommunityKind = (typeof COMMUNITY_KINDS)[number];

/**
 * Who can see a community (M28). Private: members only, and everyone else gets the same 404
 * as a missing page. Public: everyone once MOGZO launches; read-only while coming soon is on.
 */
export const COMMUNITY_VISIBILITIES = ['public', 'private'] as const;
export type CommunityVisibility = (typeof COMMUNITY_VISIBILITIES)[number];

/**
 * `owner`: the game's developer, or the team community's maker; can't be removed.
 * `manager`: added by a manager; invites people and accepts who joins.
 * `tester`: joined a test community. `member`: joined a team community.
 */
export const MEMBER_ROLES = ['owner', 'manager', 'tester', 'member'] as const;
export type MemberRole = (typeof MEMBER_ROLES)[number];
export const MANAGER_ROLES: readonly MemberRole[] = ['owner', 'manager'];
export const isManagerRole = (role: MemberRole | null | undefined) =>
  role != null && MANAGER_ROLES.includes(role);

/** A team community's address: `mogzo.com/team/<address>`. */
export const teamAddressSchema = z
  .string()
  .trim()
  .toLowerCase()
  .min(3, 'Use at least 3 characters')
  .max(40, 'Use 40 characters or fewer')
  .regex(/^[a-z0-9]+(?:-[a-z0-9]+)*$/, 'Use lower-case letters, digits and single dashes');

export const newTeamCommunitySchema = z.object({
  name: z.string().trim().min(2, 'Use at least 2 characters').max(60),
  slug: teamAddressSchema,
  about: z.string().trim().max(500).default(''),
});

/** `build`: posted by MOGZO when a build is ready, with the game playable inside it. */
export const POST_KINDS = ['text', 'build'] as const;
export type PostKind = (typeof POST_KINDS)[number];

export const POST_TAGS = ['bug', 'idea', 'question', 'other'] as const;
export type PostTag = (typeof POST_TAGS)[number];
export const POST_TAG_NAMES: Record<PostTag, string> = {
  bug: 'Bug',
  idea: 'Idea',
  question: 'Question',
  other: 'Other',
};

export const FEED_SORTS = ['hot', 'new', 'top'] as const;
export type FeedSort = (typeof FEED_SORTS)[number];

export const POST_BODY_MAX = 2000;
export const COMMENT_BODY_MAX = 2000;
/** Replies nest three levels (0, 1, 2); a reply to the deepest level joins that level. */
export const COMMENT_MAX_DEPTH = 2;

/** Testers must be adults: test builds haven't been reviewed by MOGZO. */
export const TESTER_MIN_AGE = 18;

export const INVITE_DAYS = [1, 7, 30] as const;
export const INVITE_MAX_USES = [1, 10, 50] as const;

/** Pictures per post, and the largest file accepted (it's re-encoded smaller). */
export const POST_IMAGES_MAX = 4;
export const POST_IMAGE_BYTES_MAX = 10 * 1024 * 1024;
export const POST_IMAGE_TYPES = ['image/png', 'image/jpeg', 'image/webp', 'image/gif'] as const;
/** Uploads are re-encoded by the worker; the composer waits for `ready`. */
export const POST_IMAGE_STATUSES = ['processing', 'ready', 'failed'] as const;
export type PostImageStatus = (typeof POST_IMAGE_STATUSES)[number];

export const newPostSchema = z
  .object({
    body: z.string().trim().max(POST_BODY_MAX).default(''),
    tag: z.enum(POST_TAGS).optional(),
    imageIds: z.array(z.uuid()).max(POST_IMAGES_MAX).optional(),
  })
  .refine((post) => post.body.length > 0 || Boolean(post.imageIds?.length), {
    message: 'Write something or add a picture first',
    path: ['body'],
  });

export const newCommentSchema = z.object({
  body: z.string().trim().min(1, 'Write something first').max(COMMENT_BODY_MAX),
  parentId: z.uuid().optional(),
});

export const newInviteSchema = z.object({
  days: z.union(INVITE_DAYS.map((d) => z.literal(d))),
  maxUses: z.union(INVITE_MAX_USES.map((n) => z.literal(n))),
});
